import { useState } from 'react';
import { apiUpload } from '../../data/api';

// Reusable create/edit modal driven by a field spec. Works on the mapped-entity shape;
// the caller converts to the API body on save.
export default function EntityEditor({ title, fields, initial, onSave, onClose }) {
  const [v, setV] = useState(() => ({ ...initial }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, val) => setV(prev => ({ ...prev, [k]: val }));

  const onImage = (imgKey, urlKey) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const { fileId, url } = await apiUpload(file);
      setV(prev => ({ ...prev, [imgKey]: fileId, [urlKey]: url }));
    } catch (err) { setError(err.message || 'Image upload failed.'); }
  };

  const save = async () => {
    if (saving) return;
    setError(null);
    setSaving(true);
    try { await onSave(v); onClose(); }
    catch (err) { setError(err.message || 'Save failed.'); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:24, background:'rgba(6,40,45,0.45)', backdropFilter:'blur(6px)', WebkitBackdropFilter:'blur(6px)', overflowY:'auto' }}>
      <div style={{ width:'100%', maxWidth:560, padding:30, borderRadius:24, background:'rgba(255,255,255,0.9)', backdropFilter:'blur(30px) saturate(180%)', WebkitBackdropFilter:'blur(30px) saturate(180%)', border:'1px solid rgba(255,255,255,0.9)', boxShadow:'0 30px 70px rgba(6,40,45,0.35)', animation:'floatUp .3s ease both', margin:'auto' }}>
        <div className="serif" style={{ fontSize:26, marginBottom:18 }}>{title}</div>
        <div style={{ display:'grid', gap:14, gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))' }}>
          {fields.map(f => {
            if (f.type === 'toggle') {
              const on = !!v[f.key];
              return (
                <div key={f.key} style={{ gridColumn:'1 / -1', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'12px 14px', borderRadius:13, background:'rgba(255,255,255,0.7)', border:'1px solid #f2e2d2' }}>
                  <span style={{ fontWeight:700, fontSize:14 }}>{f.label}</span>
                  <div onClick={() => set(f.key, !on)} style={{ width:52, height:30, borderRadius:100, padding:3, cursor:'pointer', display:'flex', flexShrink:0, background: on ? 'linear-gradient(135deg,#26c6a3,#0e8a9c)' : '#d7e6e2', justifyContent: on ? 'flex-end' : 'flex-start' }}>
                    <div style={{ width:24, height:24, borderRadius:'50%', background:'#fff', boxShadow:'0 2px 6px rgba(6,40,45,0.3)' }}></div>
                  </div>
                </div>
              );
            }
            if (f.type === 'sources') {
              const urls = (v[f.key] || '').split('\n').map(s => s.trim()).filter(s => /^https?:\/\//.test(s));
              return (
                <div key={f.key} style={{ gridColumn:'1 / -1' }}>
                  <span className="a-label">{f.label}</span>
                  <textarea rows={f.rows || 3} value={v[f.key] ?? ''} onChange={e => set(f.key, e.target.value)}
                    placeholder={'https://…  (one URL per line)'} className="a-input" style={{ resize:'vertical' }} />
                  {urls.length > 0 && (
                    <div style={{ display:'grid', gap:4, marginTop:6 }}>
                      {urls.map(u => <a key={u} href={u} target="_blank" rel="noreferrer" style={{ fontSize:12.5, color:'#0e8a9c', fontWeight:700, overflowWrap:'anywhere' }}>{u}</a>)}
                    </div>
                  )}
                </div>
              );
            }
            if (f.type === 'image') {
              const url = v[f.urlKey] || v.img;
              return (
                <div key={f.key} style={{ gridColumn:'1 / -1' }}>
                  <span className="a-label">{f.label}</span>
                  {url
                    ? <div style={{ height:130, borderRadius:14, backgroundImage:`url(${url})`, backgroundSize:'cover', backgroundPosition:'center', marginBottom:8 }}></div>
                    : <div style={{ height:130, borderRadius:14, border:'1px dashed rgba(14,138,122,0.4)', background:'rgba(255,255,255,0.55)', display:'flex', alignItems:'center', justifyContent:'center', color:'#5a726f', fontWeight:700, fontSize:13, marginBottom:8 }}>No image yet</div>}
                  <label className="a-ghost" style={{ display:'block', textAlign:'center', padding:10, borderRadius:12, fontSize:13.5, cursor:'pointer' }}>
                    Upload image
                    <input type="file" accept="image/*" onChange={onImage(f.key, f.urlKey)} style={{ display:'none' }} />
                  </label>
                </div>
              );
            }
            return (
              <label key={f.key} style={{ display:'block', gridColumn: f.full ? '1 / -1' : 'auto' }}>
                <span className="a-label">{f.label}</span>
                {f.type === 'textarea'
                  ? <textarea rows={f.rows || 3} value={v[f.key] ?? ''} onChange={e => set(f.key, e.target.value)} className="a-input" style={{ resize:'vertical' }} />
                  : f.type === 'select'
                  ? <select value={v[f.key] ?? ''} onChange={e => set(f.key, e.target.value)} className="a-input">{f.options.map(o => <option key={o} value={o}>{o}</option>)}</select>
                  : <input type={f.type === 'number' ? 'number' : 'text'} value={v[f.key] ?? ''} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder || ''} className="a-input" />}
              </label>
            );
          })}
        </div>
        {error && <div style={{ color:'#c0492f', fontSize:13.5, fontWeight:700, marginTop:12 }}>{error}</div>}
        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <span onClick={onClose} className="a-ghost" style={{ flex:1, textAlign:'center', padding:13, borderRadius:13, fontSize:14, cursor:'pointer' }}>Cancel</span>
          <button onClick={save} disabled={saving} className="a-primary" style={{ flex:1, padding:13, borderRadius:13, fontSize:14, opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// Field specs per entity kind.
export const FIELDS = {
  tours: [
    { key:'name', label:'Tour name', full:true },
    { key:'place', label:'Location' },
    { key:'dur', label:'Duration', placeholder:'Full day' },
    { key:'price', label:'Price ($)', type:'number' },
    { key:'desc', label:'Description', type:'textarea', full:true },
    { key:'sources', label:'Source references (where the facts came from)', type:'sources', full:true },
    { key:'imageFileId', urlKey:'img', label:'Image', type:'image' },
    { key:'isActive', label:'Active (shown on the site)', type:'toggle' },
  ],
  cruises: [
    { key:'name', label:'Cruise name', full:true },
    { key:'area', label:'Sailing area', placeholder:'Caribbean' },
    { key:'nights', label:'Length', placeholder:'7–10 nights' },
    { key:'from', label:'Price from ($)', type:'number' },
    { key:'sortOrder', label:'Order', type:'number' },
    { key:'desc', label:'Card description', type:'textarea', full:true },
    { key:'body', label:'Page copy', type:'textarea', rows:8, full:true },
    { key:'ports', label:'Typical ports (separate with ·)', type:'textarea', full:true },
    { key:'sources', label:'Source references (where the facts came from)', type:'sources', full:true },
    { key:'imageFileId', urlKey:'img', label:'Image', type:'image' },
    { key:'isActive', label:'Active (shown on the site)', type:'toggle' },
  ],
  countryGuides: [
    { key:'countryName', label:'Country name', full:true },
    { key:'key', label:'URL key (must match the browse slug)', placeholder:'dominican-republic', full:true },
    { key:'overviewText', label:'Overview — intro', type:'textarea', rows:3, full:true },
    { key:'overviewItems', label:'Overview — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'bestTimeText', label:'Best time to visit — intro', type:'textarea', rows:3, full:true },
    { key:'bestTimeItems', label:'Best time to visit — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'resortsText', label:'Resorts — intro', type:'textarea', rows:3, full:true },
    { key:'resortsItems', label:'Resorts — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'hotelsText', label:'Hotels — intro', type:'textarea', rows:3, full:true },
    { key:'hotelsItems', label:'Hotels — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'excursionsText', label:'Excursions — intro', type:'textarea', rows:3, full:true },
    { key:'excursionsItems', label:'Excursions — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'currencyText', label:'Currency — intro', type:'textarea', rows:3, full:true },
    { key:'currencyItems', label:'Currency — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'weatherText', label:'Weather — intro', type:'textarea', rows:3, full:true },
    { key:'weatherItems', label:'Weather — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'visaText', label:'Visa requirements — intro', type:'textarea', rows:3, full:true },
    { key:'visaItems', label:'Visa requirements — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'tipsText', label:'Travel tips — intro', type:'textarea', rows:3, full:true },
    { key:'tipsItems', label:'Travel tips — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'faqText', label:'FAQ — intro', type:'textarea', rows:3, full:true },
    { key:'faqItems', label:'FAQ — bullets (one per line)', type:'textarea', rows:4, full:true },
    { key:'sources', label:'Source references (where the facts came from)', type:'sources', full:true },
    { key:'isActive', label:'Active (used on the site)', type:'toggle' },
  ],
  events: [
    { key:'name', label:'Event name', full:true },
    { key:'key', label:'URL key', placeholder:'rio-carnival' },
    { key:'when', label:'When', placeholder:'February–March' },
    { key:'place', label:'Place', placeholder:'Rio de Janeiro, Brazil', full:true },
    { key:'sortOrder', label:'Order', type:'number' },
    { key:'body', label:'Page copy (last paragraph may start with "Book by:")', type:'textarea', rows:8, full:true },
    { key:'sources', label:'Source references (where the facts came from)', type:'sources', full:true },
    { key:'imageFileId', urlKey:'img', label:'Image', type:'image' },
    { key:'isActive', label:'Active (shown on the site)', type:'toggle' },
  ],
  posts: [
    { key:'title', label:'Title', full:true },
    { key:'cat', label:'Category' },
    { key:'mins', label:'Read minutes', type:'number' },
    { key:'excerpt', label:'Excerpt', type:'textarea', full:true },
    { key:'body', label:'Body', type:'textarea', rows:6, full:true },
    { key:'sources', label:'Source references (where the facts came from)', type:'sources', full:true },
    { key:'imageFileId', urlKey:'img', label:'Cover image', type:'image' },
    { key:'isPublished', label:'Published', type:'toggle' },
  ],
  testimonials: [
    { key:'name', label:'Author name' },
    { key:'initial', label:'Initial', placeholder:'S' },
    { key:'trip', label:'Trip label', full:true },
    { key:'text', label:'Testimonial', type:'textarea', full:true },
    { key:'rating', label:'Rating (1–5)', type:'number' },
    { key:'isVisible', label:'Visible on the site', type:'toggle' },
  ],
  banners: [
    { key:'title', label:'Title', full:true },
    { key:'sub', label:'Subtitle', full:true },
    { key:'state', label:'State', type:'select', options:['Draft', 'Scheduled', 'Live'] },
    { key:'position', label:'Position', type:'number' },
    { key:'imageFileId', urlKey:'img', label:'Image', type:'image' },
  ],
};

export const emptyEntity = {
  tours: { name:'', place:'', dur:'Full day', price:'', desc:'', sources:'', imageFileId:null, isActive:true },
  cruises: { name:'', area:'', nights:'', from:'', sortOrder:0, desc:'', body:'', ports:'', sources:'', imageFileId:null, isActive:true },
  countryGuides: { countryName:'', key:'', overviewText:'', overviewItems:'', bestTimeText:'', bestTimeItems:'', resortsText:'', resortsItems:'', hotelsText:'', hotelsItems:'', excursionsText:'', excursionsItems:'', currencyText:'', currencyItems:'', weatherText:'', weatherItems:'', visaText:'', visaItems:'', tipsText:'', tipsItems:'', faqText:'', faqItems:'', sources:'', isActive:true },
  events: { name:'', key:'', place:'', when:'', sortOrder:0, body:'', sources:'', imageFileId:null, isActive:true },
  posts: { title:'', cat:'Guide', mins:5, excerpt:'', body:'', sources:'', imageFileId:null, isPublished:true },
  testimonials: { name:'', initial:'', trip:'', text:'', rating:5, isVisible:true },
  banners: { title:'', sub:'', state:'Draft', position:0, imageFileId:null },
};
